import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import { getFirestoreTemplates } from '../services/templateService';
import { duplicateFromTemplate } from '../services/campaignService';
import { useUser } from '../userContext';
import { notifyError } from '../utils/notify';
import GameTemplateCard from '../components/GameTemplateCard';
import ModalBasic from '../components/ModalBasic';
import { gameTypes } from '../data/GameTypes';

export default function Games() {
  const navigate = useNavigate();
  const { businessProfile } = useUser();

  const [copyingTemplateId, setCopyingTemplateId] = useState(null);
  const [confirmExample, setConfirmExample] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [firestoreTemplates, setFirestoreTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFirestoreTemplates()
      .then(t => setFirestoreTemplates(t))
      .finally(() => setLoading(false));
  }, []);

  const openConfirm = (template) => {
    setConfirmExample(template);
    setTimeout(() => setConfirmOpen(true), 50);
  };

  const handleConfirmUseTemplate = async () => {
    const template = confirmExample;
    if (!template || !businessProfile?.id || copyingTemplateId) return;

    setCopyingTemplateId(template.campaignId);
    try {
      const result = await duplicateFromTemplate(
        template.campaignId,
        businessProfile.id,
        template.name
      );
      setConfirmOpen(false);
      navigate(`/studio/campaign/${result.id}`);
    } catch (error) {
      console.error('Failed to copy template:', error);
      notifyError('Failed to copy template. Please try again.');
    } finally {
      setCopyingTemplateId(null);
    }
  };

  const templates = useMemo(() => {
    return firestoreTemplates.map(t => {
      const display = gameTypes.find((gt) => gt.id === t.gameType)?.name;
      return {
        id: t.id,
        name: t.name,
        badgeLabel: display || t.gameType || '',
        imageUrl: t.imageUrl,
        playUrl: `/p/${t.campaignId}`,
        primaryLabel: 'Start from this template',
        primaryIcon: <DocumentDuplicateIcon className="w-3.5 h-3.5" />,
        onPrimaryClick: () => openConfirm(t),
        primaryLoading: copyingTemplateId === t.campaignId,
        primaryLoadingLabel: 'Setting up your game\u2026',
      };
    });
  }, [copyingTemplateId, firestoreTemplates]);

  return (
    <div className="gch-content-width gch-page">
      <header className="mb-3 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight gch-text-primary">
          Game Templates
        </h1>
        <p className="mt-1 text-sm gch-text-secondary max-w-2xl">
          Start with a pre-built design, or preview a live demo before creating your own.
        </p>
      </header>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="aspect-[9/16] animate-pulse bg-white/5 rounded-xl" />)}
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm gch-text-muted text-center py-12">No templates available yet.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {templates.map((t) => (
            <GameTemplateCard
              key={t.id}
              id={t.id}
              badgeLabel={t.badgeLabel}
              imageUrl={t.imageUrl}
              playUrl={t.playUrl}
              primaryLabel={t.primaryLabel}
              primaryIcon={t.primaryIcon}
              onPrimaryClick={t.onPrimaryClick}
              primaryLoading={t.primaryLoading}
              primaryLoadingLabel={t.primaryLoadingLabel}
            />
          ))}
        </div>
      )}

      {confirmExample && (
        <ModalBasic
          id="confirm-use-template"
          title={confirmExample.name || 'Use this template'}
          modalOpen={confirmOpen}
          setModalOpen={setConfirmOpen}
        >
          <div className="px-5 py-4">
            <p className="text-sm gch-text-secondary mb-5">
              You&apos;ll be taken to your new campaign where you can set up your game.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setConfirmOpen(false); setConfirmExample(null); }}
                className="btn-admin-outline btn-admin-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmUseTemplate}
                disabled={!!copyingTemplateId}
                className="btn-admin-primary btn-admin-md disabled:opacity-50"
              >
                {copyingTemplateId ? 'Setting up your game\u2026' : 'Create my campaign'}
              </button>
            </div>
          </div>
        </ModalBasic>
      )}
    </div>
  );
}
